import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { QRCodeDisplay } from '@/components/referral/QRCodeDisplay';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Download, Share2 } from 'lucide-react';
import { getDisplayName, getInitials } from '@/lib/userDisplayUtils';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';

interface MemberCardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MemberCardModal = ({ open, onOpenChange }: MemberCardModalProps) => {
  const { user, member } = useAuth();
  const [referralCode, setReferralCode] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchReferralCode = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('club_members')
        .select('referral_code')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching referral code:', error);
        return;
      }

      if (data?.referral_code) {
        setReferralCode(data.referral_code);
      }
    };

    if (open) {
      fetchReferralCode();
    }
  }, [user, open]);

  const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleDownload = async () => {
    const cardElement = document.getElementById('member-card-modal');
    if (!cardElement) return;

    setIsExporting(true);
    
    try {
      const avatarImg = cardElement.querySelector('img') as HTMLImageElement;
      const originalSrc = avatarImg?.src;
      
      if (avatarImg && originalSrc) {
        const base64Image = await convertImageToBase64(originalSrc);
        avatarImg.src = base64Image;
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const canvas = await html2canvas(cardElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      
      if (avatarImg && originalSrc) {
        avatarImg.src = originalSrc;
      }
      
      const link = document.createElement('a');
      link.download = `carte-${getDisplayName(member!)}.png`;
      link.href = canvas.toDataURL();
      link.click();
      
      toast.success('Carte téléchargée avec succès !');
    } catch (error) {
      console.error('Error downloading card:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/join/${referralCode}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rejoignez First Tuesday',
          text: 'Rejoignez notre communauté d\'entrepreneurs !',
          url: url,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papiers !');
    }
  };

  if (!member || !referralCode) {
    return null;
  }

  const displayName = getDisplayName(member);
  const initials = getInitials(member);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <div id="member-card-modal" className="p-6 bg-gradient-to-br from-background to-primary/5 rounded-lg">
          <div className="text-center space-y-6">
            <div className="relative inline-block">
              <Avatar className="h-32 w-32 mx-auto border-4 border-primary/20">
                <AvatarImage src={member.image} alt={displayName} />
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full shadow-lg">
                🤝 Networking
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-2">{displayName}</h2>
              {member.role && member.company && (
                <p className="text-muted-foreground text-lg">
                  {member.role} chez {member.company}
                </p>
              )}
            </div>

            <div className="bg-background p-6 rounded-lg shadow-inner border">
              <QRCodeDisplay 
                value={`${window.location.origin}/join/${referralCode}`}
                size={200}
              />
              <p className="mt-4 text-sm text-muted-foreground font-mono">
                {referralCode}
              </p>
            </div>

            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              📱 <strong>Membres</strong> : scannez pour vous connecter • <strong>Non-membres</strong> : scannez pour rejoindre le club
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          <Button onClick={handleDownload} className="flex-1" size="lg" disabled={isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Export en cours...' : 'Télécharger'}
          </Button>
          <Button onClick={handleShare} variant="outline" className="flex-1" size="lg">
            <Share2 className="mr-2 h-4 w-4" />
            Partager
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};